import React, { useCallback, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import {
  PeriodMetrics,
  ProperAssignedVisit,
  ProperPreSaleMetrics,
  ProperPreSaleVisit,
  ProperV2Metrics,
} from '../models/dashboardModels';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  getDashboardMetricsPreSaleEndpoint,
  getDashboardMetricsV2Endpoint,
  getDashboardPreSaleVisitsEndpoint,
  getDashboardVisitsEndpoint,
} from '../services/dashboardService';
import useAuth from '../../../hooks/useAuth';
import useAdminPagination from '../../../hooks/useAdminPagination';
import AdminPaginator from '../../../components/admin/AdminPaginator';
import DashboardVisitListTable from './DashboardVisitsListTable';
import DashboardPreSaleVisitListTable from './DashboardPreSaleVisitsListTable';
import {
  toStatsMetrics,
  VISIT_STATUS_MAP,
  PRE_SALE_STATUS_MAP,
} from '../adapters/dashboardAdapters';
import MetricsDashboard from './MetricsDashboard';
import NavigationTabDashboard from './NavigationTabDashboard';
import { activeTab } from '../types/dashboard.types';
import MetricsDateDashboard from './MetricsDateDashboard';
import DashboardFilterBar from './DashboardFilterBar';

const ProperDashboard: React.FC = () => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [activeTab, setActiveTabState] = useState<activeTab>('rent');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');

  const handleDateRangeChange = (newStart: string, newEnd: string) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatus('');
  };

  const setActiveTab = (tab: activeTab) => {
    setActiveTabState(tab);
    handleClearFilters();
  };

  const defaultPeriod: PeriodMetrics = {
    end: '',
    start: '',
  };

  const getDashboardMetrics = useCallback(
    () =>
      getDashboardMetricsV2Endpoint(token, 'proper', undefined, {
        startDate: activeTab === 'rent' ? startDate || undefined : undefined,
        endDate: activeTab === 'rent' ? endDate || undefined : undefined,
        status: activeTab === 'rent' ? status || undefined : undefined,
      }),
    [token, activeTab, startDate, endDate, status],
  );
  const [dashboardMetrics] = useAdminEntity<ProperV2Metrics>(
    getDashboardMetrics,
    console.error,
  );

  const getDashboardPreSaleMetrics = useCallback(
    () =>
      getDashboardMetricsPreSaleEndpoint(token, 'proper', {
        startDate:
          activeTab === 'pre-sale' ? startDate || undefined : undefined,
        endDate: activeTab === 'pre-sale' ? endDate || undefined : undefined,
        status: activeTab === 'pre-sale' ? status || undefined : undefined,
      }),
    [token, activeTab, startDate, endDate, status],
  );

  const [dashboardPreSaleMetrics] = useAdminEntity<ProperPreSaleMetrics>(
    getDashboardPreSaleMetrics,
    console.error,
  );

  const [dashboardVisits, setDashboardVisits] = useState<ProperAssignedVisit[]>(
    [],
  );
  const getDashboardVisits = useCallback(
    (queryParams: string, token: string) => {
      const extraParams: string[] = [];
      if (activeTab === 'rent' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'rent' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'rent' && status)
        extraParams.push(`status=${status}`);
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardVisitsEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [activeTab, startDate, endDate, status],
  );
  const { paginator, loadItems: dashboardVisitLoadItems } = useAdminPagination(
    getDashboardVisits,
    setDashboardVisits,
    console.error,
  );

  const [dashboardPreSaleVisits, setDashboardPreSaleVisits] = useState<
    ProperPreSaleVisit[]
  >([]);
  const getDashboardPreSaleVisits = useCallback(
    (queryParams: string, token: string) => {
      const extraParams: string[] = [];
      if (activeTab === 'pre-sale' && startDate)
        extraParams.push(`start_date=${startDate}`);
      if (activeTab === 'pre-sale' && endDate)
        extraParams.push(`end_date=${endDate}`);
      if (activeTab === 'pre-sale' && status)
        extraParams.push(`status=${status}`);
      const separator = extraParams.length
        ? `${queryParams.includes('?') ? '&' : '?'}${extraParams.join('&')}`
        : '';
      return getDashboardPreSaleVisitsEndpoint(
        `${queryParams}${separator}`,
        token,
      );
    },
    [activeTab, startDate, endDate, status],
  );
  const { paginator: paginatorPreSaleVisit, loadItems: itemsPreSaleVisits } =
    useAdminPagination(
      getDashboardPreSaleVisits,
      setDashboardPreSaleVisits,
      console.error,
    );

  if (!dashboardMetrics || !dashboardPreSaleMetrics) {
    return <>Cargando</>;
  }

  const rentalInfo = toStatsMetrics(dashboardMetrics.visits, VISIT_STATUS_MAP);
  const preSaleInfo = toStatsMetrics(dashboardPreSaleMetrics.visits, PRE_SALE_STATUS_MAP);

  return (
    <Container className="mt-6" fluid>
      <NavigationTabDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        preSaleCount={preSaleInfo.total}
        rentCount={rentalInfo.total}
        secondaryCount={0}
      />

      <DashboardFilterBar
        activeTab={activeTab}
        isRestricted={false}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        status={status}
        onStatusChange={setStatus}
        onClearFilters={handleClearFilters}
        hideAssignmentControl
      />

      <MetricsDateDashboard
        activeTab={activeTab}
        period={
          activeTab === 'rent'
            ? (dashboardMetrics?.period ?? defaultPeriod)
            : (dashboardPreSaleMetrics?.period ?? defaultPeriod)
        }
      />

      <MetricsDashboard
        activeTab={activeTab}
        stats={activeTab === 'rent' ? rentalInfo : preSaleInfo}
      />

      {activeTab === 'rent' && (
        <Row>
          <Col sm={12}>
            {dashboardVisits && dashboardVisits.length > 0 && (
              <DashboardVisitListTable
                visits={dashboardVisits}
                footer={
                  <AdminPaginator
                    page={paginator.page}
                    totalPages={paginator.pageCount}
                    onPageChange={dashboardVisitLoadItems}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}

      {activeTab === 'pre-sale' && (
        <Row>
          <Col sm={12}>
            {dashboardPreSaleVisits && dashboardPreSaleVisits.length > 0 && (
              <DashboardPreSaleVisitListTable
                visits={dashboardPreSaleVisits}
                footer={
                  <AdminPaginator
                    page={paginatorPreSaleVisit.page}
                    totalPages={paginatorPreSaleVisit.pageCount}
                    onPageChange={itemsPreSaleVisits}
                  />
                }
              />
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProperDashboard;
